import { useState } from 'react'
import { RxExclamationTriangle } from 'react-icons/rx'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { red } from '~/common/styles/colors'
import { Button } from '~/components/ui/atoms/button/Button'

interface TableErrorStateProps {
  error: Error
  onRetry: () => void | Promise<unknown>
}

/**
 * Error state rendered when a query needed to load a table fails: a heading, the underlying
 * error message, and a retry button. Used as the fallback of DossTable's error boundary and by
 * TableDetailsProvider when the table schema query errors, so it must not consume
 * useTableDetails.
 */
export const TableErrorState = ({ error, onRetry }: TableErrorStateProps) => {
  const { t } = useTypedTranslation()
  const [retrying, setRetrying] = useState(false)

  const handleRetry = async () => {
    setRetrying(true)
    try {
      await onRetry()
    } catch {
      // The owning query surface re-renders with the fresh error; nothing to handle here
    } finally {
      setRetrying(false)
    }
  }

  return (
    <div className="flex size-full flex-col items-center justify-center gap-3 p-4">
      <div className="flex items-center gap-2">
        <RxExclamationTriangle className="shrink-0" color={red[11]} size={16} />
        <h2>{t('table.failed_to_load_table')}</h2>
      </div>
      <pre className="max-h-40 max-w-lg overflow-auto whitespace-pre-wrap rounded-[2px] border border-red-11 bg-red-3 p-2 font-mono text-xs">
        {error.message}
      </pre>
      <Button disabled={retrying} onClick={handleRetry} size="small" variant="secondary">
        {t('common.actions.retry')}
      </Button>
    </div>
  )
}
